import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, categoryContentMap, allMovies } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

const sortOptions = [
  { label: '默认排序', value: 'default' },
  { label: '评分最高', value: 'rating' },
  { label: '最新发布', value: 'newest' },
  { label: '最多播放', value: 'popular' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState('default');
  const [showSortMenu, setShowSortMenu] = useState(false);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const categoryMovies = useMemo(() => {
    let movies: MovieItem[] = [];
    if (slug && categoryContentMap[slug]) {
      movies = [...categoryContentMap[slug]];
    } else {
      movies = [...allMovies];
    }

    const uniqueMovies = movies.filter(
      (m, i, arr) => arr.findIndex((x) => x.id === m.id) === i
    );

    if (sortBy === 'rating') {
      return uniqueMovies.sort((a, b) => b.rating - a.rating);
    }
    if (sortBy === 'newest') {
      return uniqueMovies.sort((a, b) => b.year - a.year);
    }
    return uniqueMovies;
  }, [slug, sortBy]);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        <BreadcrumbNav
          customPath={[
            { name: '首页', path: '/' },
            { name: currentCategory.name },
          ]}
        />

        {/* Category Header */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100">
                <i className={`${currentCategory.icon} text-2xl md:text-3xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {categoryMovies.length} 部作品
                </p>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200/60'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Sort */}
            <div className="flex items-center justify-between">
              <div className="relative">
                <button
                  onClick={() => setShowSortMenu(!showSortMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-200/60 rounded-lg text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-sort-desc"></i>
                  {sortOptions.find((o) => o.value === sortBy)?.label}
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${showSortMenu ? 'rotate-180' : ''}`}></i>
                </button>
                {showSortMenu && (
                  <div className="absolute top-full mt-1 left-0 bg-background-50 border border-background-200/60 rounded-lg shadow-lg z-20 min-w-[160px]">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSortBy(option.value);
                          setShowSortMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap first:rounded-t-lg last:rounded-b-lg ${
                          sortBy === option.value
                            ? 'text-primary-500 bg-primary-50'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-foreground-500">
                共 {categoryMovies.length} 部
              </p>
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="py-6 md:py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            {categoryMovies.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
                  <i className="ri-inbox-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无内容，敬请期待</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                {categoryMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-xl mb-3">
                      <img
                        src={movie.coverImage}
                        alt={movie.title}
                        className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                        style={{ aspectRatio: '2/3' }}
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                        <span className="text-xs text-background-50 bg-primary-500/90 px-2 py-1 rounded-md whitespace-nowrap">
                          {movie.updateEpisode}
                        </span>
                      </div>
                      {movie.isHot && (
                        <div className="absolute top-2 right-2">
                          <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded-md whitespace-nowrap">
                            热播
                          </span>
                        </div>
                      )}
                      {movie.isNew && (
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded-md whitespace-nowrap">
                            NEW
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1 mb-1">
                      {movie.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-400"></i>
                        {movie.rating}
                      </span>
                      <span className="whitespace-nowrap">{movie.year}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}